import { effect, inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess, Query, QueryBuilder, QueryFilter } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import {
  addDateFilter,
  addPeriodColumns,
  addDbPercentColumn,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import { formatPeriodLabel } from '../helpers/chart-theme';
import {
  InventoryValues,
  loadInventoryValues,
  resolveInventoryValues,
  withInventoryValues,
} from '../helpers/inventory-values';
import { ApiRow } from '../types/api-row';
import { SalesOrganisationComparisonRow, SalesOrganisationDetailRow } from '../types/chart-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

interface LoadResult {
  data: ApiRow[];
}

@Injectable()
export class SalesOrganisationChartService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);

  private salesOrganisationsCache = new Map<string, SalesOrganisationComparisonRow[]>();
  private salesOrganisationCache = new Map<string, SalesOrganisationDetailRow[]>();
  /** Lagerwert values per row cache key — same key, separate query. */
  private inventoryCache = new Map<string, Promise<Map<string, InventoryValues>>>();

  readonly salesOrganisationRows = signal<SalesOrganisationComparisonRow[]>([]);
  readonly singleSalesOrganisationRows = signal<SalesOrganisationDetailRow[]>([]);
  readonly loading = signal(false);
  readonly hasError = signal(false);
  lastParamsKey = '';

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.salesOrganisationsCache.clear();
      this.salesOrganisationCache.clear();
      this.inventoryCache.clear();
      this.lastParamsKey = '';
    });
  }

  async loadSalesOrganisations(
    period: DateRange,
    referencePeriod: DateRange | null,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal
  ): Promise<SalesOrganisationComparisonRow[]> {
    const cacheKey = JSON.stringify({
      period,
      referencePeriod,
      documentType,
      statisticType,
    });
    const ds = new SalesStatisticsGridDataSource(
      this.dataAccess,
      documentType,
      statisticType
    );

    const base = new QueryBuilder<ApiRow>()
      .start(0)
      .end(2000)
      .column('sales_organisation.sales_organisation', { usage: 'group' });
    applyActiveFilters(base, this.queryService);
    addValueColumns(base);
    addDbPercentColumn(base);


    const cached = this.salesOrganisationsCache.get(cacheKey);
    if (cached) {
      return this.applyInventory(cached, ds, cacheKey, period, referencePeriod);
    }

    let currentData: ApiRow[];
    let refData: ApiRow[];

    if (referencePeriod) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, period, referencePeriod);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult;
      const split = splitByPeriod(result?.data ?? []);
      currentData = split.current;
      refData = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, period.from, period.to);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult;
      currentData = result?.data ?? [];
      refData = [];
    }

    const keyOf = (r: ApiRow): string =>
      String(r['sales_organisation']?.['sales_organisation'] ?? '');
    const currentByKey = new Map(currentData.map((r) => [keyOf(r), r]));
    const refByKey = new Map(refData.map((r) => [keyOf(r), r]));
    const allKeys = [...new Set([...currentByKey.keys(), ...refByKey.keys()])];

    const rows: SalesOrganisationComparisonRow[] = allKeys
      .map((vkforg) => {
        const o = currentByKey.get(vkforg);
        const ref = refByKey.get(vkforg);
        const oNet = valFromRow(o, 'net');
        const oWes = valFromRow(o, 'cost');
        const oDb = valFromRow(o, 'contribution_margin');
        const oDbPct = valFromRow(o, 'contribution_margin_percent');
        const refNet = referencePeriod ? valFromRow(ref, 'net') : oNet;
        const refDb = referencePeriod ? valFromRow(ref, 'contribution_margin') : oDb;
        const refDbPct = referencePeriod
          ? valFromRow(ref, 'contribution_margin_percent')
          : oDbPct;
        return {
          field: vkforg,
          minRevenue: Math.min(oNet, refNet),
          dbPos: refNet < oNet ? oNet - refNet : 0,
          dbNeg: refNet > oNet ? refNet - oNet : 0,
          revenue: oNet,
          referenceRevenue: refNet,
          wes: oWes,
          db: oDb,
          dbPercent: oDbPct,
          referenceDb: refDb,
          minDb: Math.min(oDb, refDb),
          dbDeltaPos: refDb < oDb ? oDb - refDb : 0,
          dbDeltaNeg: refDb > oDb ? refDb - oDb : 0,
          referenceDbPercent: refDbPct,
          minDbPercent: Math.min(oDbPct, refDbPct),
          dbPercentDeltaPos: refDbPct < oDbPct ? oDbPct - refDbPct : 0,
          dbPercentDeltaNeg: refDbPct > oDbPct ? refDbPct - oDbPct : 0,
          // Filled in by applyInventory.
          inventoryValue: 0,
          referenceInventoryValue: 0,
          minInventoryValue: 0,
          inventoryDeltaPos: 0,
          inventoryDeltaNeg: 0,
        };
      })
      .sort((a: SalesOrganisationComparisonRow, b: SalesOrganisationComparisonRow) => b.revenue - a.revenue);
    this.salesOrganisationsCache.set(cacheKey, rows);
    return this.applyInventory(rows, ds, cacheKey, period, referencePeriod);
  }

  /** Adds the Lagerwert numbers to already-mapped rows, loading them with one
      narrow query (cached per parameter set) the first time they're needed. */
  private async applyInventory(
    rows: SalesOrganisationComparisonRow[],
    ds: SalesStatisticsGridDataSource,
    cacheKey: string,
    period: DateRange,
    referencePeriod: DateRange | null
  ): Promise<SalesOrganisationComparisonRow[]> {
    if (!this.queryService.inventoryEnabled()) return rows;
    const keys = [...new Set(rows.map((row) => String(row.field ?? '')))].filter(Boolean);
    if (keys.length === 0) return rows;
    // Grouping column only, window sized from the keys (one row per key and
    // period with a comparison period).
    const inventoryBase = new QueryBuilder<ApiRow>()
      .start(0)
      .end(keys.length * 2 + 10)
      .column('sales_organisation.sales_organisation', { usage: 'group' });
    applyActiveFilters(inventoryBase, this.queryService);
    const values = await resolveInventoryValues(this.inventoryCache, cacheKey, () =>
      loadInventoryValues(
        ds,
        inventoryBase,
        'sales_organisation.sales_organisation',
        period,
        referencePeriod,
        // No caller signal — the promise is shared between loads.
        { keys }
      )
    );
    if (!values) return rows;
    return rows.map((row) =>
      withInventoryValues(row, values.get(String(row.field ?? '')))
    );
  }

  async loadSalesOrganisation(
    period: DateRange,
    referencePeriod: DateRange | null,
    salesOrganisation: string,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal
  ): Promise<SalesOrganisationDetailRow[]> {
    const cacheKey = JSON.stringify({
      period,
      referencePeriod,
      salesOrganisation,
      documentType,
      statisticType,
    });
    const cached = this.salesOrganisationCache.get(cacheKey);
    if (cached) return cached;

    const ds = new SalesStatisticsGridDataSource(
      this.dataAccess,
      documentType,
      statisticType
    );

    const base = new QueryBuilder<ApiRow>()
      .start(0)
      .end(2)
      .column('sales_organisation.sales_organisation', { usage: 'group' });
    addValueColumns(base);
    addDbPercentColumn(base);

    base.filter({
      type: 'stringEquals',
      name: 'sales_organisation.sales_organisation',
      value: salesOrganisation,
    } as QueryFilter<ApiRow>);
    applyActiveFilters(base, this.queryService);

    let currentData: ApiRow[];
    let refData: ApiRow[];

    if (referencePeriod) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, period, referencePeriod);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult;
      const split = splitByPeriod(result?.data ?? []);
      currentData = split.current;
      refData = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, period.from, period.to);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult;
      currentData = result?.data ?? [];
      refData = [];
    }

    const allRows: (ApiRow & { period: string })[] = [
      ...refData.map((o: ApiRow) => ({ ...o, period: formatPeriodLabel(referencePeriod) })),
      ...currentData.map((o: ApiRow) => ({ ...o, period: formatPeriodLabel(period) })),
    ];

    const rows: SalesOrganisationDetailRow[] = allRows.map((o) => {
      const net = valFromRow(o, 'net');
      const cost = valFromRow(o, 'cost');
      const db = valFromRow(o, 'contribution_margin');
      return {
        field: o['sales_organisation']?.['sales_organisation'],
        period: o.period,
        revenue: net,
        wes: cost,
        db,
        db_percent: valFromRow(o, 'contribution_margin_percent'),
      };
    });
    this.salesOrganisationCache.set(cacheKey, rows);
    return rows;
  }
}
